import { useState, useEffect } from 'react';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import { supabase } from '@/integrations/supabase/external-client';

/**
 * Live unread badge count for the signed-in customer.
 *
 * Reads the SAME table that powers the Notifications inbox
 * (public.notifications on the external Supabase). Filters:
 *   - recipient_type = 'customer'
 *   - recipient_id   = customer.id
 *   - channel        = 'in_app'   (push rows are not displayed)
 *   - is_read        = false
 *
 * Live updates come from two sources:
 *   1. Postgres realtime on `notifications` for any change matching this customer.
 *   2. A `customer-notifications-updated` window CustomEvent dispatched by the
 *      foreground push handler so the badge refreshes the moment a push lands.
 */
export const NOTIFICATIONS_REFRESH_EVENT = 'customer-notifications-updated';

export function useUnreadNotifications() {
  const { customer } = useCustomerAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!customer?.id) {
      setCount(0);
      return;
    }

    const customerId = String(customer.id);
    let cancelled = false;

    const fetchCount = async () => {
      // Wait until the auth session token is actually available — without it,
      // RLS will silently filter every row out and we'll be stuck at 0.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session?.access_token) return;

      const { count: c, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', customerId)
        .eq('recipient_type', 'customer')
        .eq('channel', 'in_app')
        .eq('is_read', false);
      if (cancelled) return;
      if (error) {
        console.error('[useUnreadNotifications] count error:', error.message, error);
        return;
      }
      setCount(c || 0);
    };

    fetchCount();

    const onLocalRefresh = () => fetchCount();
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onLocalRefresh);

    const channel = supabase
      .channel(`notifications-unread-${customerId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${customerId}`,
        },
        () => fetchCount(),
      )
      .subscribe();

    return () => {
      cancelled = true;
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onLocalRefresh);
      supabase.removeChannel(channel);
    };
  }, [customer?.id]);

  return count;
}
